/* ==========================================================================
   Black Mango — Premium Animations (Awwwards-level)
   Layers on top of existing scroll-animations.js.
   Requires GSAP + ScrollTrigger globals.
   No conflicts: magnetic buttons handled by magnetic-buttons.js,
   parallax handled by scroll-animations.js.
   ========================================================================== */

(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* ====================================================================
     1. CHARACTER SPLIT REVEAL — Premium heading entrance
     Add data-split-reveal to any heading for per-character stagger.
     ==================================================================== */

  function initSplitReveal() {
    const headings = document.querySelectorAll('[data-split-reveal]');
    if (!headings.length) return;

    for (const heading of headings) {
      if (heading.dataset.splitDone) continue;
      heading.dataset.splitDone = '1';

      const text = heading.textContent.trim();
      heading.textContent = '';
      heading.style.overflow = 'hidden';

      const words = text.split(' ');
      for (let w = 0; w < words.length; w++) {
        if (w > 0) {
          const space = document.createElement('span');
          space.innerHTML = '&nbsp;';
          space.style.display = 'inline-block';
          heading.appendChild(space);
        }
        const wordWrap = document.createElement('span');
        wordWrap.style.display = 'inline-block';
        wordWrap.style.overflow = 'hidden';

        for (const char of words[w]) {
          const span = document.createElement('span');
          span.textContent = char;
          span.className = 'split-char';
          span.style.display = 'inline-block';
          span.style.willChange = 'transform, opacity';
          wordWrap.appendChild(span);
        }
        heading.appendChild(wordWrap);
      }

      const chars = heading.querySelectorAll('.split-char');
      gsap.set(chars, { y: '110%', opacity: 0 });

      ScrollTrigger.create({
        trigger: heading,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(chars, {
            y: '0%',
            opacity: 1,
            duration: 0.8,
            stagger: 0.025,
            ease: 'power4.out'
          });
        }
      });
    }
  }

  /* ====================================================================
     2. LINE REVEAL — Horizontal lines that draw in on scroll
     Add data-line-reveal to any element.
     ==================================================================== */

  function initLineReveal() {
    const lines = document.querySelectorAll('[data-line-reveal]');
    if (!lines.length) return;

    for (const line of lines) {
      if (line.style.position === '') line.style.position = 'relative';

      const pseudo = document.createElement('span');
      pseudo.className = 'line-reveal-bar';
      pseudo.style.cssText =
        'position:absolute;bottom:0;left:0;width:100%;height:1px;' +
        'background:rgba(255,255,255,0.15);transform:scaleX(0);transform-origin:left;' +
        'will-change:transform;';
      line.appendChild(pseudo);

      ScrollTrigger.create({
        trigger: line,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          gsap.to(pseudo, {
            scaleX: 1,
            duration: 1.2,
            ease: 'power4.inOut'
          });
        }
      });
    }
  }

  /* ====================================================================
     3. IMAGE CLIP REVEAL — Images reveal with clip-path wipe
     Add data-clip-reveal to image containers.
     ==================================================================== */

  function initClipReveal() {
    const containers = document.querySelectorAll('[data-clip-reveal]');
    if (!containers.length) return;

    for (const container of containers) {
      gsap.set(container, { clipPath: 'inset(0 100% 0 0)' });

      const img = container.querySelector('img');
      if (img) {
        gsap.set(img, { scale: 1.3 });
      }

      ScrollTrigger.create({
        trigger: container,
        start: 'top 80%',
        once: true,
        onEnter: function () {
          const tl = gsap.timeline();
          tl.to(container, {
            clipPath: 'inset(0 0% 0 0)',
            duration: 1.2,
            ease: 'power4.inOut'
          });
          if (img) {
            tl.to(img, {
              scale: 1,
              duration: 1.4,
              ease: 'power2.out'
            }, 0);
          }
        }
      });
    }
  }

  /* ====================================================================
     4. STAGGER GRID — Items animate in with stagger
     Add data-stagger-grid to a container with child items.
     ==================================================================== */

  function initStaggerGrid() {
    const grids = document.querySelectorAll('[data-stagger-grid]');
    if (!grids.length) return;

    for (const grid of grids) {
      const items = grid.children;
      if (!items.length) continue;

      gsap.set(items, { opacity: 0, y: 40, scale: 0.95 });

      ScrollTrigger.create({
        trigger: grid,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(items, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.7,
            stagger: {
              each: 0.08,
              from: 'start'
            },
            ease: 'power3.out'
          });
        }
      });
    }
  }

  /* ====================================================================
     5. SMOOTH FADE SLIDE — Elegant section entrance
     Add data-fade-slide="up|left|right" to any element.
     ==================================================================== */

  function initFadeSlide() {
    const elements = document.querySelectorAll('[data-fade-slide]');
    if (!elements.length) return;

    for (const el of elements) {
      const dir = el.dataset.fadeSlide || 'up';
      const from = { opacity: 0 };

      if (dir === 'up') { from.y = 60; }
      else if (dir === 'left') { from.x = -60; }
      else if (dir === 'right') { from.x = 60; }

      gsap.set(el, from);

      ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(el, {
            x: 0,
            y: 0,
            opacity: 1,
            duration: 1,
            ease: 'power3.out'
          });
        }
      });
    }
  }

  /* ====================================================================
     6. HOVER GLOW — Premium glow effect on hover (desktop only)
     Add data-hover-glow to any element.
     ==================================================================== */

  function initHoverGlow() {
    if (window.innerWidth < 1024) return;

    const elements = document.querySelectorAll('[data-hover-glow]');
    if (!elements.length) return;

    for (const el of elements) {
      if (el.style.position === '') el.style.position = 'relative';
      el.style.overflow = 'hidden';

      const glow = document.createElement('span');
      glow.style.cssText =
        'position:absolute;width:200px;height:200px;border-radius:50%;' +
        'background:radial-gradient(circle,rgba(240,99,6,0.15) 0%,transparent 70%);' +
        'pointer-events:none;transform:translate(-50%,-50%);opacity:0;' +
        'filter:blur(30px);will-change:transform,opacity;z-index:0;';
      el.appendChild(glow);

      el.addEventListener('mouseenter', function () {
        gsap.to(glow, { opacity: 1, duration: 0.4 });
      });

      el.addEventListener('mousemove', function (e) {
        const rect = el.getBoundingClientRect();
        gsap.set(glow, {
          left: e.clientX - rect.left,
          top: e.clientY - rect.top
        });
      });

      el.addEventListener('mouseleave', function () {
        gsap.to(glow, { opacity: 0, duration: 0.4 });
      });
    }
  }

  /* ====================================================================
     7. COUNTER ANIMATION — Animate numbers counting up
     Add data-count-up to any element containing a number.
     ==================================================================== */

  function initCountUp() {
    const counters = document.querySelectorAll('[data-count-up]');
    if (!counters.length) return;

    for (const el of counters) {
      const target = parseFloat(el.textContent.replace(/[^0-9.]/g, ''));
      if (isNaN(target)) continue;

      const prefix = el.textContent.match(/^[^0-9]*/)[0] || '';
      const suffix = el.textContent.match(/[^0-9]*$/)[0] || '';
      const decimals = (el.textContent.match(/\.(\d+)/) || ['', ''])[1].length;

      const obj = { val: 0 };

      ScrollTrigger.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          gsap.to(obj, {
            val: target,
            duration: 1.8,
            ease: 'power2.out',
            onUpdate: function () {
              el.textContent = prefix + obj.val.toFixed(decimals) + suffix;
            }
          });
        }
      });
    }
  }

  /* ====================================================================
     INIT — Run all modules after DOM + existing animations are ready
     ==================================================================== */

  function init() {
    initSplitReveal();
    initLineReveal();
    initClipReveal();
    initStaggerGrid();
    initFadeSlide();
    initHoverGlow();
    initCountUp();
  }

  /* Delay slightly to let scroll-animations.js initialize first */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestAnimationFrame(init);
    });
  } else {
    requestAnimationFrame(init);
  }
})();
